import type { PlanId } from '@/config/access-control'
import { normalizePlanId } from '@/config/plans'
import { useAuthStore } from '@/stores/authStore'
import { useBillingStatus, useSubscriptionOrganizationId } from '@/stores/subscriptionStore'
import { resolveActiveOrganization } from '@/utils/subscriptionHelpers'

/**
 * Resolves the current plan id for the active organization.
 * Precedence:
 *   1. Billing status (Chargebee-backed) current_plan
 *   2. Organization.subscription.plan_name on the auth store user
 *   3. null (unknown — e.g. platform deployments without billing)
 */
export function useCurrentPlan(): PlanId | null {
	const billing = useBillingStatus()
	const billingOrgId = useSubscriptionOrganizationId()
	const user = useAuthStore(s => s.user)
	const currentOrgId = useAuthStore(s => s.currentOrganizationId)
	const currentTenant = useAuthStore(s => s.currentTenant)

	// Cached per org and persisted, so after a switch it can still describe the org we left. Gating
	// the new org on the old tier is worse than not knowing it, so fall through until a refresh lands.
	const billingMatchesOrg = !currentOrgId || !billingOrgId || billingOrgId === currentOrgId
	const fromBilling = billingMatchesOrg && billing?.current_plan ? normalizePlanId(billing.current_plan) : null
	if (fromBilling) {
		return fromBilling as PlanId
	}

	const resolvedOrg = resolveActiveOrganization(user, currentOrgId, currentTenant)
	const fromOrg = resolvedOrg?.subscription?.plan_name ? normalizePlanId(resolvedOrg.subscription.plan_name) : null
	return fromOrg as PlanId | null
}
